<template lang="pug">
  .panel-page-white
    .panel.mx-auto.body-1
      FormErrorAlert(
        :message="message"
        v-if="message"
        @dismiss="message = ''")
      h1 Agency Usage Report
      p The following report will provide usage at the agency level.
      form(
        @submit.prevent="submit"
        autocomplete="off"
      )
        FormHeader.mt-2(
          title="LOCATION"
          v-if="$auth.internalAdmin()")
        .form-section.mt-2(v-if="$auth.internalAdmin()")
          .form-row
            .col
                .form-row
                  label(for="statesList") State(s)
                .form-row
                  .col
                    SuperSelect(
                      taggable
                      :options="filterStates"
                      label-name="name"
                      value-name="abbreviation"
                      v-model="selectedStates"
                      @filter="stateFilter = $event"
                      @input="refreshAgencies"
                    )
        
        FormHeader(title="AGENCIES")
        .form-section.mt-2
          .form-row
            .col
              CustomRadio(
                model-value="all"
                v-model="agencyType"
              )  All Agencies
          .form-row
            .col
              CustomRadio(
                model-value="select"
                v-model="agencyType"
              )  Selected Agencies
          .form-row.mt-2(v-if="agencyType === 'select'")
            .col
              SuperSelect(
                :options="agencyList"
                taggable
                label-name="full_description"
                value-name="id"
                v-model="selectedAgencies"
                @filter="filter"
              )
    
        FormHeader(title="DATE RANGE")
        .form-section
          .form-row
            .col
              CustomRadio(
                v-model="dateRangeType"
                model-value="30"
              ) 30 Days
          .form-row.mt-2
            .col
              CustomRadio(
                v-model="dateRangeType"
                model-value="60"
              ) 60 Days
          .form-row.mt-2
            .col
              CustomRadio(
                v-model="dateRangeType"
                model-value="90"
              ) 90 Days
          .form-row.mt-2
            .col
              CustomRadio(
                v-model="dateRangeType"
                model-value="specified"
              ) Specified Dates
          .form-row.mt-2(v-if="dateRangeType === 'specified'")
            .col.ml-4
              p Date range must be within the past 3 years.
              .form-row.w-50
                .col
                  .form-group
                    label(for="start-date") From
                    Datepicker#start-date(
                      :input-class="datepickerClass"
                      format="MM/DD/YYYY"
                      v-model="startDate"
                      :disabled-dates="disabledDates"
                    )
                .col
                  .form-group
                    label(for="end-date") To
                    Datepicker#end-date(
                      :input-class="datepickerClass"
                      format="MM/DD/YYYY"
                      v-model="endDate"
                      :disabled-dates="disabledDates"
                    )
        .form-row.mt-2
          .col
            button.btn.btn-primary(type="submit") Submit
</template>

<script>
import { toggle } from '@/lib/utils'
import CustomRadio from '@/components/CustomRadio'
import { getAgencies } from '@/lib/services/agencies'
import axios from '@/lib/axios'
import debounce from '@/lib/debounce'
import states from '../lib/states'
import store from '@/store'
import FormHeader from '@/components/FormHeader'
import SuperSelect from '@/components/SuperSelect'
import Datepicker from '@/components/Datepicker'
import { globalError } from '@/lib/utils'
import {
  format,
  subDays,
  subYears,
  parse,
  differenceInCalendarDays,
  isAfter,
} from 'date-fns/esm'
import stdmsg from '@/lib/stdmsg'
import * as validations from '@/lib/validation'
import { loadForm, formItem } from '@/lib/formstore'

export default {
  name: 'AgencyUsageSearch',
  data() {
    return {
      message: '',
      agencyList: [],
      selectedAgencies: [],
      stateFilter: '',
      filter: debounce(value => this.filterQuery(value), 250),
      disabledDates: {
        to: subYears(new Date(), 3),
        from: new Date(),
      },
      agencyType: '',
      dateRangeType: '30',
      startDate: format(subDays(new Date(), 29), 'MM/dd/yyyy'),
      endDate: format(new Date(), 'MM/dd/yyyy'),
      selectedStates: [],
      formSubmitted: false,
    }
  },
  computed: {
    agencies() {
      if (this.agencyList) return this.agencyList
      else return []
    },
    dateRangeInvalid() {
      let startDt = parse(this.startDate, 'MM/dd/yyyy', new Date())
      let endDt = parse(this.endDate, 'MM/dd/yyyy', new Date())
      return (
        this.formSubmitted &&
        (Math.abs(differenceInCalendarDays(startDt, new Date())) > 3 * 365 ||
          Math.abs(differenceInCalendarDays(endDt, new Date())) > 3 * 365 ||
          isAfter(startDt, endDt))
      )
    },
    datepickerClass() {
      return { 'form-control': true, 'is-invalid': this.dateRangeInvalid }
    },
    filterStates() {
      return states.filter(st =>
        st.name.toLowerCase().includes(this.stateFilter.toLowerCase()),
      )
    },
  },
  methods: {
    ...validations.validationHelpers(),
    submit() {
      this.formSubmitted = true
      this.markAllTouched()
      if (this.dateRangeInvalid) {
        scrollTo(0, 0)
        this.message =
          'The date range may be a maximum of 3 years from now, and From date must be before To date.'
        return
      }
      this.$router.push({ name: 'agency_usage_report' })
    },
    agencyParams(name) {
      let p = { name }
      const extraStates = this.selectedStates.map(x => x.abbreviation).join(',')
      if (extraStates) {
        if (p.state == undefined) {
          p.state = ''
        }
        p.state = [p.state, extraStates].filter(x => x).join(',')
      }
      return p
    },
    refreshAgencies(value) {
      this.filterQuery(value)
    },
    toggle: function(status) {
      // If this looks weird, it's because, for some reason, the status variable in the store wasn't updating
      // so I use the spread operator to duplicate the status then reassign it, forcing an update. /shrug
      this.status = [...toggle(this.status, status)]
    },
    getSearchAgencies(name) {
      getAgencies({ name: name || '' })
        .then(resp => (this.agencyList = resp.data))
        .catch(err => globalError(err))
    },
    filterQuery(value) {
      if (this.$auth.internalAdmin()) {
        axios({ loaderDelay: 0 })
          .get('/api/misc/agencies', { params: this.agencyParams(value) })
          .then(resp => (this.agencyList = resp.data))
          .catch(err => globalError(err))
      } else {
        this.getSearchAgencies(value)
      }
    },
    checkRefreshAgencies() {
      let item = formItem('agencyusage', 'selectedStates') || ''
      if (item.length > 0) {
        this.refreshAgencies(formItem('agencyusage', 'selectedStates'))
      } else {
        this.getSearchAgencies()
      }
    },
  },
  watch: {
    dateRangeType(val) {
      let days = 30
      if (['30', '60', '90'].includes(val)) {
        days = parseInt(val)
        this.startDate = format(subDays(new Date(), days - 1), 'MM/dd/yyyy')
        this.endDate = format(new Date(), 'MM/dd/yyyy')
      }
    },
  },
  mounted() {
    loadForm(
      'agencyusage',
      'agencyType',
      'dateRangeType',
      'startDate',
      'endDate',
      'selectedAgencies',
      'selectedStates',
    ).bind(this)()
    this.checkRefreshAgencies()
    this.agencyType = this.agencyType || 'all'
    this.selectedAgencies =
      this.selectedAgencies.length > 0 ? this.selectedAgencies : []
  },
  components: { FormHeader, CustomRadio, SuperSelect, Datepicker },
}
</script>

<style lang="scss" scoped>
.form-section {
  padding: 0.5rem;
  margin-top: 1rem;
  margin-bottom: 1rem;
  background: #f8f8f8;
}
</style>
